import React, { useState, useMemo } from "react";
import { base44 } from "@/race-tracker/api/rtClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import RacePicker from "../components/sweep/RacePicker";
import SweepResults from "../components/sweep/SweepResults";

export default function IntelSweep() {
  const queryClient = useQueryClient();
  const [selectedRaceId, setSelectedRaceId] = useState(null);
  const [sweepingRaces, setSweepingRaces] = useState(new Set());
  const [bulkProgress, setBulkProgress] = useState(null); // {current, total}

  const { data: races = [] } = useQuery({
    queryKey: ["races"],
    queryFn: () => base44.entities.Race.list("-created_date", 5000),
  });
  const { data: advertisers = [] } = useQuery({
    queryKey: ["advertisers"],
    queryFn: () => base44.entities.Advertiser.list("-created_date", 500),
  });
  const { data: opponents = [] } = useQuery({
    queryKey: ["opponents"],
    queryFn: () => base44.entities.Opponent.list("-created_date", 10000),
  });
  const { data: sweepResults = [] } = useQuery({
    queryKey: ["sweepResults"],
    queryFn: () => base44.entities.SweepResult.list("-sweep_timestamp", 5000),
  });

  const adsMap = useMemo(() => {
    const m = {};
    advertisers.forEach(a => { m[a.id] = a; });
    return m;
  }, [advertisers]);

  const sweepMap = useMemo(() => {
    const m = {};
    sweepResults.forEach(s => { m[s.race_id] = s; });
    return m;
  }, [sweepResults]);

  const opponentsByRace = useMemo(() => {
    const m = {};
    opponents.forEach(o => {
      if (!m[o.race_id]) m[o.race_id] = [];
      m[o.race_id].push(o);
    });
    return m;
  }, [opponents]);

  const activeRaces = useMemo(() =>
    races.filter(r => r.status === "Active"),
    [races]
  );

  const selectedRace = useMemo(() =>
    activeRaces.find(r => r.id === selectedRaceId) || null,
    [activeRaces, selectedRaceId]
  );

  const runSweep = async (raceId) => {
    setSweepingRaces((prev) => new Set([...prev, raceId]))
    try {
      await base44.functions.invoke("runSweep", { race_id: raceId });
      queryClient.invalidateQueries({ queryKey: ["sweepResults"] });
      queryClient.invalidateQueries({ queryKey: ["sweepRuns", raceId] });
      queryClient.invalidateQueries({ queryKey: ["races"] });
    } finally {
      setSweepingRaces((prev) => {
        const next = new Set(prev);
        next.delete(raceId);
        return next;
      });
    }
  };

  const runBulkSweep = async (racesToSweep) => {
    setBulkProgress({ current: 0, total: racesToSweep.length });
    for (let i = 0; i < racesToSweep.length; i++) {
      setBulkProgress({ current: i + 1, total: racesToSweep.length });
      await runSweep(racesToSweep[i].id);
    }
    setBulkProgress(null);
  };

  return (
    <div className="flex flex-col md:flex-row gap-0 h-[calc(100vh-130px)] min-h-[600px]">
      {/* Left Panel */}
      <div className="w-full md:w-[30%] md:min-w-[260px] md:max-w-[340px] border-r border-border overflow-y-auto shrink-0 bg-card">
        <RacePicker
          races={activeRaces}
          adsMap={adsMap}
          opponentsByRace={opponentsByRace}
          sweepMap={sweepMap}
          sweepingRaces={sweepingRaces}
          selectedRaceId={selectedRaceId}
          onSelectRace={setSelectedRaceId}
          onBulkSweep={runBulkSweep}
          bulkProgress={bulkProgress}
        />
      </div>

      {/* Right Panel */}
      <div className="flex-1 overflow-y-auto bg-background">
        {selectedRace ? (
          <SweepResults
            race={selectedRace}
            advertiser={adsMap[selectedRace.advertiser_id] || null}
            opponents={opponentsByRace[selectedRace.id] || []}
            sweepResult={sweepMap[selectedRace.id] || null}
            isSweeping={sweepingRaces.has(selectedRace.id)}
            onRunSweep={() => runSweep(selectedRace.id)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-12">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <span className="text-2xl">🔍</span>
            </div>
            <p className="text-sm font-medium text-muted-foreground">Select a race to view sweep results</p>
            <p className="text-xs text-muted-foreground mt-1">Choose a race from the left panel to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}